import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { useSocketContext } from '@techchange/components';

const axiosClient = axios.create({
	withCredentials: true,
});

/**
 * React hook that keeps track of a point total, including any real-time updates.
 *
 * @param   {Object} params                  The base query params.
 * @param   {String} params.user_id          The user id.
 * @param   {String} params.scope            The scope of the point total.
 * @param   {String} params.scope_id         The scope ID of the point total.
 * @param   {String} params.organization_id  The organization ID of the point total.
 *
 * @returns {Array} The point total data and a callback function to fetch the data.
 */
const usePointTotal = (params = {}) => {

	const [data, setData] = useState(null);

	const { socket } = useSocketContext();

	const fetchData = useCallback(() => {

		axiosClient.get('/api/v2/point-totals', {
			params: {
				scope: params.scope,
				scope_id: params.scope_id,
				user_id: params.user_id,
			}
		}).then((response) => {

			const pointTotals = response && response.data;

			// Check if the user has already earned some points
			if (pointTotals && pointTotals.length === 1) {

				setData(pointTotals[0]);

			// Otherwise, handle the case of no points for the user
			} else {

				setData({
					total: 0,
					user_id: params.user_id,
					scope: params.scope,
					scope_id: params.scope_id,
					organization_id: params.organization_id,
				});

			}

		}).catch((err) => {
			console.error(err);
		});

	}, [params.scope, params.scope_id, params.organization_id, params.user_id]);

	// Listen for real-time changes to the point total
	useEffect(() => {

		if (!data) {
			return () => {};
		}

		const handleRealTimeUpdate = (pointLog) => {

			// Make sure that the point total changed for ONLY the current user!
			if (pointLog.user_id !== params.user_id) {
				return;
			}

			// Find the updated point total
			const updatedPointTotal = pointLog.point_totals && pointLog.point_totals.find((total) =>
				total.scope === params.scope &&
				total.scope_id === params.scope_id &&
				total.organization_id === params.organization_id
			);

			if (updatedPointTotal) {

				setData((prevData) => Object.assign({}, prevData, { total: updatedPointTotal.total }));

			}

		};

		// Use the socket to start listening for real-time changes
		socket?.on('create:point-log', handleRealTimeUpdate);

		// Clean up the socket to stop listening for real-time changes
		return () => socket?.removeListener('create:point-log', handleRealTimeUpdate);

	}, [
		data,
		socket,
		params.user_id,
		params.scope,
		params.scope_id,
		params.organization_id,
	]);

	return [data, fetchData];

};

export default usePointTotal;
